import { useEffect, useMemo, useState } from 'react'

import { PAYEE_TYPE } from '../../models/Payee'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { ImageView } from '../common/ImageView'

type Props = {
    selectedEmployee: User | undefined
    onSelectEmployee: (employee: User | undefined) => void
}

export const EmployeeSearchableSelect = ({ selectedEmployee, onSelectEmployee }: Props) => {
    const [employees, setEmployees] = useState<User[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const employeesSearchableItems = useMemo(() => {
        return employees.map((employee) => ({
            id: employee.id ?? '',
            title: employee.name ?? '',
            tertiaryView: (
                <ImageView
                    image={employee.image}
                    className="size-6 rounded-full"
                    fallbackIcon={{ icon: 'user.small', background: 'round' }}
                />
            ),
            employee,
        }))
    }, [employees])

    const isDisabled = isLoading || employees.length === 0

    const selectedItem = useMemo(() => {
        if (selectedEmployee === undefined) {
            return null
        }

        return {
            id: selectedEmployee.id ?? '',
            title: selectedEmployee.name ?? '',
            tertiaryView: (
                <ImageView
                    image={selectedEmployee.image}
                    className="size-6 rounded-full"
                    fallbackIcon={{ icon: 'user.small', background: 'round' }}
                />
            ),
            employee: selectedEmployee,
        }
    }, [employeesSearchableItems, selectedEmployee])

    useEffect(() => {
        ;(async () => {
            setEmployees(await RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_ANY))
            setIsLoading(false)
        })()
    }, [])

    const placeholder = useMemo(() => {
        if (isLoading) {
            return 'Loading...'
        }
        if (employees.length === 0) {
            return 'No employees found'
        }
        return 'Type to search'
    }, [isLoading, employees])

    return (
        <SearchableSelect
            items={employeesSearchableItems}
            onSelectItem={(item) => onSelectEmployee(item?.employee)}
            selectedItemOverride={selectedItem}
            titleWeight="bold"
            placeholder={placeholder}
            disabled={isDisabled}
            isLoading={isLoading}
        />
    )
}
